import { useEffect, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { useLang } from '../i18n.js';
import { Icon } from '../shell.js';
import { navigate } from '../router.js';
import { Callout, Dialog, Loading, toast } from '../ui.js';
import { uploadFile } from './ProjectFiles.js';

/**
 * "Send for review" on a video (owner 2026-11-01, B in "Förenkla varje sida per roll"): the file and the status in one
 * step. The newest files in the project's Internal / EXPORTS / VIDEO, with the newest one whose name matches the video
 * already picked; when the file is not there yet, it is uploaded into that folder from the same dialog and picked. One
 * press sends it: a version in the Hub's review, and the video moves on with it. Review in the Hub off, a Ziflow project
 * or no Drive: the button moves the status as before (`fallback`).
 */

export interface ReviewFiles {
  on: boolean;
  reason?: string;
  video?: { id: string; name: string };
  folder?: { root: 'internal'; id: string; path: string[] };
  files?: { id: string; name: string; size: number | null; modifiedTime: string | null }[];
  suggested?: string | null;
  /** a Ziflow project: sending moves it to the Hub (owner 2026-11-02), with the videos still in a Ziflow review */
  moveToHub?: { waiting: string[] } | null;
}

const MOVE = {
  en: {
    title: 'The project moves to the Hub',
    body: 'This project is reviewed in Ziflow. When you send, it moves to the Hub: Monday is set to Hub, Ziflow is no longer used for it, and the client gets a new link from the Hub. The round number carries on from Ziflow.',
    waiting: (names: string) => `Still in a Ziflow review: ${names}. Send them from the Hub too.`,
    send: 'Move to the Hub and send'
  },
  sv: {
    title: 'Projektet flyttas till Hubben',
    body: 'Projektet granskas i Ziflow. När du skickar flyttas det till Hubben: Monday sätts till Hub, Ziflow används inte längre för projektet och kunden får en ny länk från Hubben. Rundan räknas vidare från Ziflow.',
    waiting: (names: string) => `Ligger fortfarande i granskning i Ziflow: ${names}. Skicka även dem från Hubben.`,
    send: 'Flytta till Hubben och skicka'
  }
} as const;

/** What the sender confirms before a Ziflow project's first send from the Hub. */
export function MoveToHubNote(p: { move: { waiting: string[] }; skip?: string }) {
  const t = MOVE[useLang()];
  const waiting = p.move.waiting.filter((n) => n !== p.skip);
  return (
    <Callout tone="warn" title={t.title}>
      <p class="small">{t.body}</p>
      {waiting.length ? <p class="small">{t.waiting(waiting.join(', '))}</p> : null}
    </Callout>
  );
}

export const moveToHubLabel = (lang: 'en' | 'sv') => MOVE[lang].send;

const TX = {
  en: {
    title: (v: string) => `Send for review – ${v}`,
    from: 'Files in',
    pick: 'Pick the file to send. The newest file that matches the video is already picked.',
    none: 'No files in the folder yet. Upload the export here.',
    upload: 'Upload file',
    uploading: (pct: number) => `Uploading… ${pct}%`,
    send: 'Send for review',
    sending: 'Sending…',
    cancel: 'Cancel',
    sent: (v: string) => `${v} is sent for review`,
    open: 'Open the review',
    matches: 'matches the video'
  },
  sv: {
    title: (v: string) => `Skicka till granskning – ${v}`,
    from: 'Filer i',
    pick: 'Välj filen som ska skickas. Den nyaste filen som matchar videon är redan vald.',
    none: 'Inga filer i mappen än. Ladda upp exporten här.',
    upload: 'Ladda upp fil',
    uploading: (pct: number) => `Laddar upp… ${pct} %`,
    send: 'Skicka till granskning',
    sending: 'Skickar…',
    cancel: 'Avbryt',
    sent: (v: string) => `${v} är skickad till granskning`,
    open: 'Öppna granskningen',
    matches: 'matchar videon'
  }
} as const;

const mb = (n: number | null) => (n === null ? '' : n >= 1e9 ? `${(n / 1e9).toFixed(1)} GB` : `${Math.max(1, Math.round(n / 1e6))} MB`);
const day = (iso: string | null, lang: 'en' | 'sv') => (iso ? new Date(iso).toLocaleString(lang === 'sv' ? 'sv-SE' : 'en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Stockholm' }) : '');

export function SendForReview(p: { api: Api; projectId: string; videoId: string; videoName: string; fallback: () => Promise<void>; onDone: () => Promise<void>; onClose: () => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [data, setData] = useState<ReviewFiles | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [upload, setUpload] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    p.api
      .get<ReviewFiles>(`/api/projects/${p.projectId}/videos/${p.videoId}/review-files`)
      .then(async (r) => {
        if (!live) return;
        if (!r.on) {
          // no file to pick here: the button does what it always did
          p.onClose();
          await p.fallback();
          return;
        }
        setData(r);
        setPicked(r.suggested ?? null);
      })
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [p.projectId, p.videoId]);

  const onFile = async (e: Event) => {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !data?.folder) return;
    setError(null);
    setUpload(0);
    try {
      const up = await uploadFile(p.api, p.projectId, { root: 'internal', folder: data.folder.id }, file, (sent) => setUpload(Math.floor((sent / file.size) * 100)));
      setData({ ...data, files: [{ id: up.id, name: up.name, size: file.size, modifiedTime: new Date().toISOString() }, ...(data.files ?? [])] });
      setPicked(up.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUpload(null);
    }
  };

  const send = async () => {
    if (!picked) return;
    setBusy(true);
    setError(null);
    try {
      const r = await p.api.post<{ results: { ok: boolean; error: string | null; videoId: string; versionId: string | null }[] }>(`/api/projects/${p.projectId}/review/submit`, { kind: 'draft', files: [{ fileId: picked, videoId: p.videoId }], ...(data?.moveToHub ? { moveToHub: true } : {}) });
      const res = r.results[0];
      if (!res?.ok) {
        setError(res?.error ?? '');
        return;
      }
      p.onClose();
      await p.onDone();
      const url = `/projects/${p.projectId}/review/${res.videoId}${res.versionId ? `?version=${res.versionId}` : ''}`;
      // the toast's button opens the new version
      toast({ text: t.sent(p.videoName), undoLabel: t.open, undo: () => navigate(url) });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data && !error) return null;
  const files = data?.files ?? [];
  return (
    <Dialog
      title={t.title(p.videoName)}
      onClose={p.onClose}
      sub={data?.folder ? `${t.from} ${data.folder.path.join(' / ')}` : undefined}
      foot={
        <>
          <label class={`btn sfr-up${upload !== null || busy || !data ? ' is-disabled' : ''}`}>
            <Icon name="upload" /> {upload !== null ? t.uploading(upload) : t.upload}
            <input type="file" class="sr-only" disabled={upload !== null || busy || !data} onChange={onFile} />
          </label>
          <span class="sfr-gap" />
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button type="button" class="btn btn-primary" disabled={!picked || busy || upload !== null} onClick={() => void send()}>
            {busy ? t.sending : data?.moveToHub ? moveToHubLabel(lang) : t.send}
          </button>
        </>
      }
    >
      {!data ? (
        error ? <p class="field-error small">{error}</p> : <Loading />
      ) : (
        <>
          {data.moveToHub ? <MoveToHubNote move={data.moveToHub} skip={p.videoName} /> : null}
          <p class="small muted">{files.length ? t.pick : t.none}</p>
          {files.length ? (
            <ul class="sfr-list" role="radiogroup" aria-label={t.send}>
              {files.map((f) => (
                <li>
                  <label class={`sfr-row${picked === f.id ? ' is-on' : ''}`}>
                    <input type="radio" name="sfr" checked={picked === f.id} onChange={() => setPicked(f.id)} />
                    <span class="sfr-name">
                      <strong>{f.name}</strong>
                      <span class="small muted">
                        {[day(f.modifiedTime, lang), mb(f.size), f.id === data.suggested ? t.matches : ''].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          ) : null}
          {error ? <p class="field-error small">{error}</p> : null}
        </>
      )}
    </Dialog>
  );
}
